/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import { useLiveRequests, requestAgo, requestStatusTone } from "@/components/homepage/widgets/useLiveRequests";
import { clientLabel } from "@/api/requests";

export const LiveRequestsCard: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const [showProbes, setShowProbes] = useState(false);
  const { enabled, rows, isError, baseline } = useLiveRequests();
  const visible = showProbes ? rows : rows.filter((r) => !r.probe);
  const probeCount = rows.length - rows.filter((r) => !r.probe).length;

  return (
    <section className="flex flex-col rounded-xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.03]">
      <div className="mb-3 flex items-center gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-[15px] font-semibold text-gray-900 dark:text-white">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
            Live requests
          </h3>
          <p className="text-[12px] text-gray-500 dark:text-gray-400">traffic passing through the reverse proxy</p>
        </div>
        <button
          type="button"
          onClick={onOpen}
          className="ml-auto inline-flex h-8 flex-none items-center whitespace-nowrap rounded-lg border border-gray-200 px-3 text-[12.5px] text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
        >
          Filters & details
        </button>
        {enabled && probeCount > 0 && (
          <button
            type="button"
            onClick={() => setShowProbes((v) => !v)}
            title="Nyrvex's own health checks"
            className="flex-none text-[12px] text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            {showProbes ? "hide probes" : `+${probeCount} probes`}
          </button>
        )}
      </div>

      {!enabled ? (
        <div className="py-8 text-center text-[12.5px] text-gray-500 dark:text-gray-400">
          not configured — set <code>[request_log]</code> in config
        </div>
      ) : visible.length === 0 ? (
        <div className="py-8 text-center text-[12.5px] text-gray-500 dark:text-gray-400">
          {isError ? "unavailable" : "waiting for traffic…"}
        </div>
      ) : (
        <div className="flex max-h-72 min-h-0 flex-col gap-px cc-scroll overflow-y-auto font-mono text-[11px] leading-tight">
          {visible.map((r) => (
            <div
              key={r.id}
              className={`flex items-center gap-2 py-0.5${r.id > baseline ? " req-in" : ""}`}
              title={`${r.method} ${r.host}${r.path} · ${r.status} · ${r.clientHost ? `${r.clientHost} (${r.clientIp})` : r.clientIp} · ${(+r.durationMs).toFixed(0)}ms${
                r.userAgent ? ` · ${r.userAgent}` : ""
              }`}
            >
              <span
                title={r.network}
                className={`h-1.5 w-1.5 flex-none rounded-full ${r.network === "local" ? "bg-sky-500" : r.network === "vpn" ? "bg-violet-500" : "bg-amber-500"}`}
              />
              <span className={`flex-none rounded-[3px] border px-1 tabular-nums ${requestStatusTone(r.status)}`}>
                {r.status}
              </span>
              <span className="flex-none text-gray-400 dark:text-gray-500">{r.method}</span>
              <span className="max-w-[32%] flex-none truncate font-sans font-medium text-gray-700 dark:text-gray-300">
                {r.service || r.host}
              </span>
              <span className="min-w-0 flex-1 truncate text-gray-500 dark:text-gray-400">{r.path}</span>
              <span className="hidden max-w-[26%] flex-none truncate text-gray-400 sm:inline dark:text-gray-500">{clientLabel(r)}</span>
              <span className="flex-none tabular-nums text-gray-400 dark:text-gray-500">
                {+r.durationMs < 1 ? "<1" : (+r.durationMs).toFixed(0)}ms
              </span>
              <span className="flex-none tabular-nums text-gray-300 dark:text-gray-600">{requestAgo(r.time)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
